import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";

@Injectable()
export class KeycloakAuthService {
    private readonly defaultRequiredRoles: string[] = ["Admin", "Helpdesk_L1", "Technical_Support_L2"];
    private keycloak: any;
    private initialized: boolean = false;
    private initPromise: Promise<boolean>;
    private refreshTimer: any;

    isEnabled(): boolean {
        return !!environment["keycloak"] && environment["keycloak"].enabled === true;
    }

    init(): Promise<boolean> {
        if (!this.isEnabled()) {
            this.publishAuthContext();
            return Promise.resolve(false);
        }

        if (this.initPromise) {
            return this.initPromise;
        }

        let config: any = environment["keycloak"];
        this.removeLegacyAuthParams();
        this.initPromise = this.loadAdapter()
            .then((Keycloak: any) => {
                this.keycloak = new Keycloak({
                    url: config.url,
                    realm: config.realm,
                    clientId: config.clientId
                });

                let initOptions: any = {
                    onLoad: config.onLoad || "login-required",
                    pkceMethod: config.pkceMethod || "S256",
                    checkLoginIframe: config.checkLoginIframe !== false,
                    redirectUri: this.loginRedirectUri()
                };

                // Silent SSO is only used by check-sso. Supplying its URL for
                // login-required still triggers the adapter's third-party cookie
                // probe, which can time out in browsers that block the iframe.
                if (initOptions.onLoad === 'check-sso' && config.silentCheckSsoRedirectUri) {
                    initOptions.silentCheckSsoRedirectUri = this.absoluteUrl(config.silentCheckSsoRedirectUri);
                }

                return this.keycloak.init(initOptions);
            })
            .then((authenticated: boolean) => {
                this.initialized = true;
                if (authenticated && this.hasRequiredRole()) {
                    this.syncLegacyUser();
                    this.scheduleTokenRefresh();
                } else if (authenticated) {
                    this.clearLegacySession();
                }
                this.publishAuthContext();
                return authenticated;
            })
            .catch((error: any) => {
                this.initialized = true;
                this.publishAuthContext();
                console.error("Keycloak initialization failed", error);
                return false;
            });

        return this.initPromise;
    }

    login(redirectUri?: string): Promise<any> {
        if (!this.isEnabled()) {
            return Promise.resolve(false);
        }

        let options: any = { redirectUri: this.loginRedirectUri(redirectUri) };

        if (!this.keycloak) {
            return this.init().then(() => {
                return this.keycloak ? this.keycloak.login(options) : Promise.reject("Keycloak is not initialized");
            });
        }

        return this.keycloak.login(options);
    }

    logout(redirectUri?: string): Promise<any> {
        this.clearLegacySession();

        if (!this.isEnabled() || !this.keycloak) {
            return Promise.resolve(true);
        }

        let options: any = {};
        if (redirectUri) {
            options.redirectUri = this.absoluteUrl(redirectUri);
        }

        return this.keycloak.logout(options);
    }

    isAuthenticated(): boolean {
        if (!this.isEnabled()) {
            return this.hasLegacySession();
        }

        return this.hasAuthenticatedSession() && this.hasRequiredRole();
    }

    hasAuthenticatedSession(): boolean {
        return !!this.keycloak && this.keycloak.authenticated === true;
    }

    getRoles(): string[] {
        if (!this.hasAuthenticatedSession()) {
            return [];
        }

        let roles: string[] = [];
        let realmRoles = this.keycloak.realmAccess && this.keycloak.realmAccess.roles;
        this.appendRoles(roles, realmRoles);

        // Keycloak can expose application roles either as realm roles or as
        // client roles. Accept both representations so an Admin role assigned
        // to dashboard-frontend is not treated as an unauthorised session.
        let resourceAccess = this.keycloak.resourceAccess || {};
        let config: any = environment["keycloak"] || {};
        let clientAccess = resourceAccess[config.clientId];
        this.appendRoles(roles, clientAccess && clientAccess.roles);

        return roles;
    }

    private appendRoles(target: string[], source: any): void {
        if (!(source instanceof Array)) {
            return;
        }

        source.forEach((role: string) => {
            if (target.indexOf(role) === -1) {
                target.push(role);
            }
        });
    }

    hasRole(role: string): boolean {
        return this.getRoles().indexOf(role) !== -1;
    }

    hasRequiredRole(): boolean {
        if (!this.isEnabled()) {
            return true;
        }

        let config: any = environment["keycloak"] || {};
        let requiredRoles: string[] = config.requiredRoles || this.defaultRequiredRoles;
        let roles = this.getRoles();
        return requiredRoles.some((role: string) => roles.indexOf(role) !== -1);
    }

    refreshToken(minValidity?: number): Promise<string> {
        if (!this.isEnabled()) {
            return Promise.resolve(this.getAccessToken());
        }

        if (!this.keycloak || !this.keycloak.authenticated) {
            return Promise.resolve(null);
        }

        let config: any = environment["keycloak"];
        let validity = minValidity || config.minValiditySeconds || 30;

        return this.keycloak.updateToken(validity)
            .then(() => {
                if (!this.hasRequiredRole()) {
                    this.clearLegacySession();
                    return Promise.reject("Keycloak user does not have a required role");
                }
                this.syncLegacyUser();
                this.scheduleTokenRefresh();
                return this.getAccessToken();
            })
            .catch((error: any) => {
                this.clearLegacySession();
                return Promise.reject(error);
            });
    }

    getAccessToken(): string {
        if (this.isEnabled() && this.isAuthenticated() && this.keycloak.token) {
            return this.keycloak.token;
        }

        let currentUser = this.getStoredUser();
        return currentUser ? currentUser.token : null;
    }

    getCurrentUser(): any {
        if (this.isEnabled() && this.isAuthenticated()) {
            return this.syncLegacyUser();
        }

        return this.getStoredUser();
    }

    buildHeaders(contentType?: string): any {
        let currentUser = this.getCurrentUser();
        let headers: any = {
            "Accept": "application/json"
        };

        if (currentUser && currentUser.token) {
            headers["Authorization"] = "Bearer " + currentUser.token;
        }

        if (currentUser && currentUser.consumer_name) {
            headers["X-Dashboard-Account"] = currentUser.consumer_name;
        }

        if (currentUser && currentUser.credential_username) {
            headers["X-Caligoo-Username"] = currentUser.credential_username;
        }

        if (contentType) {
            headers["Content-Type"] = contentType;
        }

        return headers;
    }

    clearLegacySession() {
        localStorage.removeItem("currentUser");
        localStorage.removeItem("currentSettings");
        localStorage.removeItem("currentAccounts");
        localStorage.removeItem("external");
        localStorage.removeItem("auth_error");
        this.publishAuthContext();
    }

    private scheduleTokenRefresh() {
        if (!this.isEnabled() || !this.keycloak || !this.keycloak.tokenParsed || !this.keycloak.tokenParsed.exp) {
            return;
        }

        if (this.refreshTimer) {
            window.clearTimeout(this.refreshTimer);
        }

        let config: any = environment["keycloak"];
        let minValidity = config.minValiditySeconds || 30;
        let refreshIn = (this.keycloak.tokenParsed.exp * 1000) - new Date().getTime() - (minValidity * 1000);
        let delay = Math.max(refreshIn, 5000);

        this.refreshTimer = window.setTimeout(() => {
            this.refreshToken(minValidity)
                .catch(() => {
                    this.clearLegacySession();
                });
        }, delay);
    }

    private loadAdapter(): Promise<any> {
        let win: any = window;
        if (win.Keycloak) {
            return Promise.resolve(win.Keycloak);
        }

        let config: any = environment["keycloak"] || {};
        let scriptUrl = config.adapterScriptUrl || "/assets/keycloak/keycloak-loader.js";
        let eventName = "mycaptive-keycloak-js-loaded";

        return new Promise((resolve, reject) => {
            let existingScript = document.querySelector("script[data-mycaptive-keycloak='true']");
            let timeout = window.setTimeout(() => {
                reject("Keycloak adapter load timeout");
            }, config.adapterLoadTimeout || 10000);

            let onLoaded = () => {
                window.clearTimeout(timeout);
                window.removeEventListener(eventName, onLoaded);
                if (win.Keycloak) {
                    resolve(win.Keycloak);
                } else {
                    reject("Keycloak adapter did not expose window.Keycloak");
                }
            };

            window.addEventListener(eventName, onLoaded);

            if (existingScript) {
                return;
            }

            let script = document.createElement("script");
            script.type = "module";
            script.src = scriptUrl;
            script.setAttribute("data-mycaptive-keycloak", "true");
            script.onerror = () => {
                window.clearTimeout(timeout);
                window.removeEventListener(eventName, onLoaded);
                reject("Unable to load Keycloak adapter from " + scriptUrl);
            };
            document.head.appendChild(script);
        });
    }

    private syncLegacyUser(): any {
        if (!this.keycloak || !this.keycloak.authenticated) {
            return null;
        }

        let tokenParsed = this.keycloak.tokenParsed || {};
        let idTokenParsed = this.keycloak.idTokenParsed || {};
        let config: any = environment["keycloak"] || {};
        let claims = config.claims || {};
        let storedUser = this.getStoredUser() || {};
        let expires = tokenParsed.exp ? new Date(tokenParsed.exp * 1000).toISOString() : storedUser.expires;

        let user: any = {
            token: this.keycloak.token,
            expires: expires,
            consumer_name: this.readClaim(tokenParsed, claims.consumerName) ||
                this.readClaim(idTokenParsed, claims.consumerName) ||
                storedUser.consumer_name,
            credential_username: this.readClaim(tokenParsed, claims.credentialUsername) ||
                this.readClaim(idTokenParsed, claims.credentialUsername) ||
                tokenParsed.preferred_username ||
                idTokenParsed.preferred_username ||
                tokenParsed.email ||
                idTokenParsed.email ||
                storedUser.credential_username,
            username: tokenParsed.preferred_username || idTokenParsed.preferred_username || storedUser.username,
            email: tokenParsed.email || idTokenParsed.email || storedUser.email,
            name: tokenParsed.name || idTokenParsed.name || storedUser.name,
            subject: this.keycloak.subject || tokenParsed.sub || idTokenParsed.sub || storedUser.subject,
            roles: this.getRoles(),
            source: "keycloak"
        };

        if (config.legacyUserStorage !== false) {
            localStorage.setItem("currentUser", JSON.stringify(user));
        }

        this.publishAuthContext();
        return user;
    }

    private readClaim(source: any, claimConfig: any): any {
        if (!source || !claimConfig) {
            return null;
        }

        if (claimConfig instanceof Array) {
            for (let claim of claimConfig) {
                let value = this.readClaim(source, claim);
                if (value) {
                    return value;
                }
            }
            return null;
        }

        let parts = ("" + claimConfig).split(".");
        let current = source;
        for (let part of parts) {
            if (!current || current[part] === undefined || current[part] === null) {
                return null;
            }
            current = current[part];
        }

        return current;
    }

    private hasLegacySession(): boolean {
        let currentUser = this.getStoredUser();
        return !!currentUser && !!currentUser.token;
    }

    private getStoredUser(): any {
        let stored = localStorage.getItem("currentUser");
        if (!stored) {
            return null;
        }

        try {
            return JSON.parse(stored);
        } catch (e) {
            return null;
        }
    }

    private loginRedirectUri(redirectUri?: string): string {
        let url = new URL(redirectUri ? this.absoluteUrl(redirectUri) : new URL('index', document.baseURI).href);
        // OAuth redirect URIs cannot contain fragments. A route captured after
        // a callback may still include Keycloak's state, session_state and code.
        // Clean the outgoing target, leaving the current callback for the adapter.
        url.hash = '';
        return url.href;
    }

    private absoluteUrl(url: string): string {
        if (!url) {
            return url;
        }

        if (url.indexOf("http://") === 0 || url.indexOf("https://") === 0) {
            return url;
        }

        let normalized = url.charAt(0) === "/" ? url : "/" + url;
        return window.location.origin + normalized;
    }

    private removeLegacyAuthParams() {
        let url = new URL(window.location.href);
        let legacyParams = ["auth", "auth_error"];
        let changed = false;

        legacyParams.forEach((param: string) => {
            if (url.searchParams.has(param)) {
                url.searchParams.delete(param);
                changed = true;
            }
        });

        if (changed) {
            window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
        }
    }

    private publishAuthContext() {
        let self = this;
        (<any>window).MyCaptiveAuth = {
            getCurrentUser: function () {
                return self.getCurrentUser();
            },
            getAccessToken: function () {
                return self.getAccessToken();
            },
            buildHeaders: function (contentType?: string) {
                return self.buildHeaders(contentType);
            },
            getRoles: function () {
                return self.getRoles();
            },
            hasRole: function (role: string) {
                return self.hasRole(role);
            },
            hasRequiredRole: function () {
                return self.hasRequiredRole();
            },
            initialized: this.initialized,
            keycloakEnabled: this.isEnabled()
        };
    }
}
